import { DeviceType } from '@appcharge/shared-ui';
import {
  DEFAULT_BODY_BLOCKS_ORDER,
  ELocalStorageKeys,
  StoreBodyBlock
} from 'constants/enums';
import {
  PANEL_MAX_WIDTH,
  PANEL_MIN_WIDTH
} from 'constants/templateStudio';

import {
  BlockMetadata,
  BundleDesignsMetadata,
  PromotionDesignsMetadata,
  RollingOffersDesignsMetadata
} from 'components/TemplatesStudio/DesignsMetadata/types';
import { EThemes } from 'components/TemplatesStudio/enums';

import { localStorageUtil } from '../utils/localStorageUtil';

import { Observable } from './observable';

export type TemplateStudioBlock =
  | StoreBodyBlock
  | 'popup'
  | 'footer'
  | 'header';

class TemplateStudioStateService {
  readonly openSidebarBlock = new Observable<TemplateStudioBlock | undefined>();
  readonly hiddenBlocks = new Observable<Set<TemplateStudioBlock>>(new Set());
  readonly previewDeviceLayout = new Observable<DeviceType>(
    localStorageUtil.getAny<string>(ELocalStorageKeys.PREVIEW_DEVICE_LAYOUT) ||
      DeviceType.MOBILE
  );
  readonly templateIdToActivate = new Observable<string | undefined>(undefined);
  readonly templateIdToDelete = new Observable<string | undefined>(undefined);
  readonly displayPopupPreview = new Observable<boolean>(false);
  readonly displayProgressBarPreview = new Observable<boolean>(false);
  readonly currentCompany = new Observable<string | undefined>(undefined);
  readonly defaultBodyBlocksOrder = new Observable<StoreBodyBlock[]>(undefined);
  readonly supportedThemes = new Observable<EThemes[]>([]);
  readonly supportedThemesForBundles = new Observable<EThemes[]>([]);
  readonly supportedThemesForPromotions = new Observable<EThemes[]>([]);
  readonly supportedThemesForRollingOffers = new Observable<EThemes[]>([]);
  readonly relevantBundleDesigns = new Observable<BundleDesignsMetadata[]>([]);
  readonly relevantPromotionDesigns = new Observable<
    PromotionDesignsMetadata[]
  >([]);
  readonly relevantRollingOffersDesigns = new Observable<
    RollingOffersDesignsMetadata[]
  >([]);
  readonly relevantDailyBonusBadgesDesigns = new Observable<BlockMetadata[]>(
    []
  );
  readonly relevantPopupsDesigns = new Observable<BlockMetadata[]>([]);
  readonly relevantProgressBarDesigns = new Observable<BlockMetadata[]>([]);
  readonly scrollToSection = new Observable<
    StoreBodyBlock | string | undefined
  >(undefined);
  readonly isBrandKitOpen = new Observable<boolean>(true);
  readonly isPersonalizationOpen = new Observable<boolean>(false);
  readonly personalizationJson = new Observable<string | undefined>(undefined);
  readonly generatedOffersJson = new Observable<string | undefined>(undefined);
  readonly previewOffersResponse = new Observable<
    Record<string, unknown> | undefined
  >(undefined);
  private static loadPanelWidth(): number {
    const stored = localStorageUtil.getAny<number>(
      ELocalStorageKeys.TEST_PERSONALIZATION_PANEL_WIDTH
    );
    return stored != null && stored >= PANEL_MIN_WIDTH && stored <= PANEL_MAX_WIDTH
      ? stored
      : PANEL_MIN_WIDTH;
  }
  readonly personalizationPanelWidth = new Observable<number>(
    TemplateStudioStateService.loadPanelWidth()
  );

  clear = () => {
    this.openSidebarBlock.set(undefined);
    this.hiddenBlocks.set(new Set());
    this.templateIdToActivate.set(undefined);
    this.templateIdToDelete.set(undefined);
    this.displayPopupPreview.set(false);
    this.currentCompany.set(undefined);
    this.defaultBodyBlocksOrder.set(DEFAULT_BODY_BLOCKS_ORDER);
    this.supportedThemes.set([]);
    this.supportedThemesForBundles.set([]);
    this.supportedThemesForPromotions.set([]);
    this.supportedThemesForRollingOffers.set([]);
    this.relevantBundleDesigns.set([]);
    this.relevantPromotionDesigns.set([]);
    this.relevantRollingOffersDesigns.set([]);
    this.relevantDailyBonusBadgesDesigns.set([]);
    this.relevantPopupsDesigns.set([]);
    this.relevantProgressBarDesigns.set([]);
    this.scrollToSection.set(undefined);
    this.isBrandKitOpen.set(false);
    this.isPersonalizationOpen.set(false);
    this.personalizationJson.set(undefined);
    this.generatedOffersJson.set(undefined);
    this.previewOffersResponse.set(undefined);
  };

  setOpenSidebarBlock = (block: TemplateStudioBlock | undefined) =>
    this.openSidebarBlock.set(block);

  toggleBlockVisibility = (block: TemplateStudioBlock) => {
    const currentHidden = this.hiddenBlocks.get();
    const newHidden = new Set(currentHidden);

    if (newHidden.has(block)) {
      newHidden.delete(block);
    } else {
      newHidden.add(block);
    }

    this.hiddenBlocks.set(newHidden);
  };

  isBlockHidden = (block: TemplateStudioBlock) => {
    return this.hiddenBlocks.get()?.has(block);
  };

  togglePreviewDeviceLayout = () => {
    const currentLayout = this.previewDeviceLayout.get();
    const newLayout =
      currentLayout === DeviceType.DESKTOP
        ? DeviceType.MOBILE
        : DeviceType.DESKTOP;

    this.previewDeviceLayout.set(newLayout);
    localStorageUtil.set(ELocalStorageKeys.PREVIEW_DEVICE_LAYOUT, newLayout);
  };

  setTemplateIdToActivate = (templateId: string | undefined) => {
    this.templateIdToActivate.set(templateId);
  };

  setTemplateIdToDelete = (templateId: string | undefined) => {
    this.templateIdToDelete.set(templateId);
  };

  setDisplayPopupPreview = (display: boolean) => {
    this.displayPopupPreview.set(display);
  };

  setCurrentCompany = (company: string | undefined) => {
    this.currentCompany.set(company);
  };

  setDefaultBodyBlocksOrder = (bodyBlocksOrder: StoreBodyBlock[]) => {
    this.defaultBodyBlocksOrder.set(bodyBlocksOrder);
  };

  setDisplayProgressBarPreview = (display: boolean) => {
    this.displayProgressBarPreview.set(display);
  };

  setSupportedThemes = (themes: EThemes[]) => {
    this.supportedThemes.set(themes);
  };

  setSupportedThemesForBundles = (themes: EThemes[]) => {
    this.supportedThemesForBundles.set(themes);
  };

  setSupportedThemesForPromotions = (themes: EThemes[]) => {
    this.supportedThemesForPromotions.set(themes);
  };

  setSupportedThemesForRollingOffers = (themes: EThemes[]) => {
    this.supportedThemesForRollingOffers.set(themes);
  };

  setRelevantBundleDesigns = (designs: BundleDesignsMetadata[]) => {
    this.relevantBundleDesigns.set(designs);
  };

  setRelevantPromotionDesigns = (designs: PromotionDesignsMetadata[]) => {
    this.relevantPromotionDesigns.set(designs);
  };

  setRelevantRollingOffersDesigns = (
    designs: RollingOffersDesignsMetadata[]
  ) => {
    this.relevantRollingOffersDesigns.set(designs);
  };

  setRelevantDailyBonusBadgesDesigns = (designs: BlockMetadata[]) => {
    this.relevantDailyBonusBadgesDesigns.set(designs);
  };

  setRelevantPopupsDesigns = (designs: BlockMetadata[]) => {
    this.relevantPopupsDesigns.set(designs);
  };

  setRelevantProgressBarDesigns = (designs: BlockMetadata[]) => {
    this.relevantProgressBarDesigns.set(designs);
  };

  setScrollToSection = (section: StoreBodyBlock | string | undefined) => {
    this.scrollToSection.set(section);
  };

  setIsBrandKitOpen = (isOpen: boolean) => {
    this.isBrandKitOpen.set(isOpen);
  };

  setIsPersonalizationOpen = (isOpen: boolean) => {
    this.isPersonalizationOpen.set(isOpen);
  };

  setPersonalizationJson = (json: string | undefined) => {
    this.personalizationJson.set(json);
  };

  setGeneratedOffersJson = (json: string | undefined) => {
    this.generatedOffersJson.set(json);
  };

  setPreviewOffersResponse = (
    response: Record<string, unknown> | undefined
  ) => {
    this.previewOffersResponse.set(response);
  };

  setPersonalizationPanelWidth = (width: number) => {
    const clamped = Math.max(
      PANEL_MIN_WIDTH,
      Math.min(PANEL_MAX_WIDTH, width)
    );
    this.personalizationPanelWidth.set(clamped);
    localStorageUtil.set(
      ELocalStorageKeys.TEST_PERSONALIZATION_PANEL_WIDTH,
      clamped
    );
  };
}

export const templateStudioStateService = new TemplateStudioStateService();
